import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Character from './components/Character';
import Background from './components/Background';
import SquareButtons from './components/SquareButtons';
import StyleButtons from './components/StyleButtons';
import CharacterScreen from './components/CharacterScreen';
import ObjectVideos from './components/ObjectVideos';
import ISwipe from './components/ISwipe';
import usePreloadAssets from './hooks/usePreloadAssets';
import BackgroundAnimation from './components/BackgroundAnimation';
import LoadingScreen from './components/LoadingScreen';
import CharacterLoading from './components/CharacterLoading';

const assets = {
  images: [
    '/amberapp/characters/surferka/surferkasprite.png',
    '/amberapp/characters/powergirl/powergirlsprite.png',
    '/amberapp/characters/aktywistka/aktywistkasprite.png',
    '/amberapp/characters/tatuazysta/tatuazystasprite.png',
    // Add other image assets
  ],
  videos: [
    '/amberapp/videos/surferka.mp4',
    '/amberapp/videos/powergirl.mp4',
    '/amberapp/videos/aktywistka.mp4',
    '/amberapp/videos/tatuazysta.mp4',
    '/amberapp/videos/surferkalc.mp4',
    '/amberapp/videos/powergirllc.mp4',
    '/amberapp/videos/aktywistkalc.mp4',
    '/amberapp/videos/tatuazystalc.mp4',
    // Add other video assets
  ]
};

const App = () => {
  const [state, setState] = useState({
    background: 0,
    lowContrast: false,
    reducedMotion: false,
    characterStyle: 'surferka',
    isFullscreen: false,
    savedTime: 0,
    isPlaying: true,
    showCharacterScreen: false,
    isInfo: false,
    videoDuration: 0,
  });

  const [styleLoading, setStyleLoading] = useState(false);
  const videoRefs = useRef([]);

  const { loaded, loadingPercentage } = usePreloadAssets(assets);

  const toggleLowContrast = () => {
    const currentVideoIndex = state.lowContrast ? state.background + 4 : state.background;
    const currentVideoElement = videoRefs.current[currentVideoIndex];
    const currentTime = currentVideoElement?.currentTime || 0;

    setState((prevState) => ({
      ...prevState,
      lowContrast: !prevState.lowContrast,
      savedTime: currentTime,
      isPlaying: !currentVideoElement.paused,
    }));
  };

  const toggleReducedMotion = () => {
    const currentVideoIndex = state.lowContrast ? state.background + 4 : state.background;
    const currentVideoElement = videoRefs.current[currentVideoIndex];
    const currentTime = currentVideoElement?.currentTime || 0;

    setState((prevState) => ({
      ...prevState,
      reducedMotion: !prevState.reducedMotion,
      savedTime: currentTime,
      isPlaying: !currentVideoElement.paused,
    }));
  };

  const handleStyleChange = (style) => {
    const currentVideoIndex = state.lowContrast ? state.background + 4 : state.background;
    const currentVideoElement = videoRefs.current[currentVideoIndex];

    // Pause and hide the current video
    if (currentVideoElement) {
      const playPromise = currentVideoElement.pause();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          currentVideoElement.style.display = 'none';
        }).catch(error => {
          console.error('Error pausing video:', error);
        });
      } else {
        currentVideoElement.style.display = 'none';
      }
    }

    setStyleLoading(true);

    setTimeout(() => {
      switch (style) {
        case 'surferka':
          setState((prevState) => ({
            ...prevState,
            background: 0,
            characterStyle: 'surferka',
          }));
          break;
        case 'powergirl':
          setState((prevState) => ({
            ...prevState,
            background: 1,
            characterStyle: 'powergirl',
          }));
          break;
        case 'aktywistka':
          setState((prevState) => ({
            ...prevState,
            background: 2,
            characterStyle: 'aktywistka',
          }));
          break;
        case 'tatuazysta':
          setState((prevState) => ({
            ...prevState,
            background: 3,
            characterStyle: 'tatuazysta',
          }));
          break;
        default:
          setState((prevState) => ({
            ...prevState,
            background: 0,
            characterStyle: 'surferka',
          }));
      }
      setStyleLoading(false);

      // Show and play the new video
      const newVideoIndex = state.lowContrast ? state.background + 4 : state.background;
      const newVideoElement = videoRefs.current[newVideoIndex];
      if (newVideoElement) {
        newVideoElement.style.display = 'block';
        newVideoElement.currentTime = state.savedTime + 0.2;
        newVideoElement.playbackRate = state.reducedMotion ? 0.2 : 1;

        const playPromise = newVideoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('Error playing video:', error);
          });
        }
      }
    }, 1000); // Adjust the delay to match the loading animation
  };

  const handleChooseCharacter = () => {
    setState((prevState) => ({
      ...prevState,
      showCharacterScreen: true,
    }));
  };

  const handleVideoEnd = () => {
    setState((prevState) => ({
      ...prevState,
      showCharacterScreen: false,
    }));
  };

  const handleDuration = (duration) => {
    setState((prevState) => ({
      ...prevState,
      videoDuration: duration,
    }));
  };

  useEffect(() => {
    if (state.isPlaying) {
      const currentVideoIndex = state.lowContrast ? state.background + 4 : state.background;
      const currentVideoElement = videoRefs.current[currentVideoIndex];
      if (currentVideoElement) {
        const adjustedTime = state.savedTime + 0.2;
        currentVideoElement.currentTime = adjustedTime;
        currentVideoElement.playbackRate = state.reducedMotion ? 0.2 : 1;

        const playPromise = currentVideoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('Error playing video:', error);
          });
        }
      }
    }
  }, [state.lowContrast, state.reducedMotion, state.background, state.savedTime, state.isPlaying]);

  if (!loaded)  {
    return <LoadingScreen loadingPercentage={loadingPercentage} />;
  }

  return (
    <div className={`App ${state.lowContrast ? 'low-contrast' : 'normal-contrast'}`}>
      <div id="app-window" className="container">
        {!state.showCharacterScreen ? (
          <>
            <Background state={state} videoRefs={videoRefs} />
            <div id="flex1">
              <StyleButtons 
                state={state}
                onStyleChange={handleStyleChange}
                lowContrast={state.lowContrast}
                isInfo={state.isInfo}
              />
            </div>
            <div id="flex2">
              {styleLoading ? (
                <CharacterLoading /> // Ensure this component shows a loading percentage
              ) : (
                <Character 
                  isLowContrast={state.lowContrast} 
                  isReducedMotion={state.reducedMotion} 
                  characterStyle={state.characterStyle}
                  lowContrast={state.lowContrast}
                  isInfo={state.isInfo}
                  onChooseCharacter={handleChooseCharacter}
                  videoDuration={state.videoDuration}
                  isSelect={false}
                />
              )}
              {state.isInfo && (<ISwipe/>)}
            </div>
            <div id="flex3">
              <ObjectVideos 
                characterStyle={state.characterStyle}
                lowContrast={state.lowContrast}
                isInfo={state.isInfo}
              /> 
              <SquareButtons
                state={state}
                setState={setState}
                toggleLowContrast={toggleLowContrast}
                toggleReducedMotion={toggleReducedMotion}
                lowContrast={state.lowContrast}
                isInfo={state.isInfo}
              />
            </div>
          </>
        ) : (
          <CharacterScreen 
            state={state}
            onVideoEnd={handleVideoEnd} 
            handleStyleChange={handleStyleChange}
            handleChooseCharacter={handleChooseCharacter}
          />
        )}
      </div>
    </div>
  );
};

export default App;
